import { Box, Card, Divider, Heading, TextBlock } from "@bcc-ui/ui-components";
import { BarElement, CategoryScale, Chart, ChartData, ChartOptions, LinearScale, Tooltip, TooltipItem } from "chart.js";
import React from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import style from "./locked-assets-distribution-card.scss";
import { LockedAssetsDistribution } from "./lockedAssetsDistribution";
import {
    LOCK_PATHS,
    LockPath,
} from "components/asset-management-system/custom-views/tabs/risk/cards/risk-factors-card/lockedDevices";
import { AssetPathKey } from "domain/asset-management/assetPathMapping";
import { createTranslatorPath } from "services/pathTranslator";
import { resolveColor } from "utils/commonFunctions";

Chart.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface Props {
    data: LockedAssetsDistribution;
}

// TODO BCC-6100 Theme the colors when dark theme colors are available
const getLockPathColor = (lockPath: LockPath): string => {
    switch (lockPath) {
        case AssetPathKey.AUTOPILOT_ENROLLMENT_STATE:
            return resolveColor("--color-grey-700");
        case AssetPathKey.FIND_MY_IPHONE:
            return "#F44E52";
    }
};

export const LockedAssetsDistributionCard = (props: Props) => {
    const { t } = useTranslation();
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");

    const totalCount = props.data.totalCount;

    const getTooltipContent = (count: number) => {
        if (count === 1) {
            return `${count} ${t("AssetManagement.asset").toLowerCase()}`;
        }

        return `${count} ${t("AssetManagement.assets").toLowerCase()}`;
    };

    const items = LOCK_PATHS.map((lockPath) => ({
        lockPath,
        text: translatedPaths(lockPath),
        color: getLockPathColor(lockPath),
        count: props.data.counts[lockPath],
    }));

    const chartData: ChartData<"bar"> = {
        labels: items.map((item) => item.text),
        datasets: [
            {
                data: items.map((item) => item.count),
                backgroundColor: items.map((item) => item.color),
                borderWidth: 0,
                borderRadius: 4,
                maxBarThickness: 56,
            },
        ],
    };

    const chartOptions: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: { display: false },
            },
            y: {
                beginAtZero: true,
                max: totalCount,
            },
        },
        plugins: {
            legend: { display: false },
            datalabels: { display: false },
            tooltip: {
                backgroundColor: resolveColor("--theme-background-layer"),
                titleColor: resolveColor("--theme-text-primary"),
                bodyColor: resolveColor("--theme-text-primary"),
                titleFont: { family: "Lato Medium, Arial, Helvetica, sans-serif", size: 14, weight: "bold" as const },
                bodyFont: { family: "Lato Medium, Arial, Helvetica, sans-serif", size: 14 },
                padding: 12,
                cornerRadius: 4,
                displayColors: true,
                callbacks: {
                    label: (tooltipItem: TooltipItem<"bar">) => getTooltipContent(tooltipItem.parsed.y),
                },
            },
        },
    };

    return (
        <Card padding={"None"} className={style.card} dataType="LOCKED_ASSETS_DISTRIBUTION">
            <Box
                paddingTop={"Xs"}
                paddingLeft={"Md"}
                paddingRight={"Md"}
                paddingBottom={"Xxs"}
                gap={"Xxs"}
                verticalAlignment={"Center"}
                justifyContent={"SpaceBetween"}
            >
                <Heading variant="SUBTITLE_1" disableTopSpacing disableBottomSpacing>
                    {t("AssetManagement.risk.lockedAssetsDistributionChart.heading")}
                </Heading>
                <TextBlock className={style.secondaryText}>
                    {totalCount === 1
                        ? `${totalCount} ${t("AssetManagement.asset").toLowerCase()}`
                        : `${totalCount} ${t("AssetManagement.assets").toLowerCase()}`}
                </TextBlock>
            </Box>

            <Divider />

            {totalCount === 0 && (
                <Box verticalAlignment="Center" horizontalAlignment="Center" className={style.emptyState}>
                    {t("AssetManagement.risk.lockedAssetsDistributionChart.emptyStateMessage")}
                </Box>
            )}

            {totalCount > 0 && (
                <Box columnLayout padding={"Md"} gap={"Md"}>
                    <Box className={style.chartContainer}>
                        <Bar data={chartData} options={chartOptions} datatype="LOCKED_ASSETS_DISTRIBUTION_CHART" />
                    </Box>

                    <Box columnLayout gap={"Xs"}>
                        {items.map((item) => {
                            return (
                                <Box
                                    key={item.lockPath}
                                    verticalAlignment="Center"
                                    justifyContent="SpaceBetween"
                                    gap={"Xxs"}
                                    className={style.label}
                                    datatype={item.lockPath}
                                >
                                    <Box gap={"Xs"} verticalAlignment="Center">
                                        <div className={style.highlightedBox} style={{ background: item.color }} />
                                        {item.text}
                                    </Box>

                                    <strong>{item.count}</strong>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Card>
    );
};
